import type { SafetyPolicyType, SequenceBranch } from "@unbox/db";

// Dependency-light (db *types* only) so it can be re-exported from `@unbox/api`
// and consumed by the frontend without pulling the DB client into the bundle.

// The five predefined safety policies every merchant gets, in display order,
// with the human-readable name used for the sequence seeded for each. Keyed by
// the DB enum so adding a policy type is a compile error until it's named here.
export const SAFETY_POLICY_LABELS: Record<SafetyPolicyType, string> = {
  end_creator_relationship: "End creator relationship",
  end_linked_collaboration: "End linked collaboration",
  request_video_removal: "Request video removal",
  request_metadata_edit: "Request metadata edit",
  warn_and_remove_from_gmv_max: "Send warning to creator and remove from GMV Max",
  warn_creator: "Warn creator",
  remove_from_gmv_max_only: "Remove from GMV Max only",
  no_action_needed: "No action required",
};

export const SAFETY_POLICY_TYPES = Object.keys(SAFETY_POLICY_LABELS) as SafetyPolicyType[];

// Policies whose only effect is a GMV Max action, so they can't run at all without
// TikTok Ads authorised — unlike a policy with a GMV Max step among others, which
// still runs (see GMV_MAX_STEP_TYPES for the per-step version).
export const SAFETY_POLICY_GMV_MAX_ONLY: Record<SafetyPolicyType, boolean> = {
  ...(Object.fromEntries(SAFETY_POLICY_TYPES.map((type) => [type, false])) as Record<SafetyPolicyType, boolean>),
  warn_and_remove_from_gmv_max: true,
  remove_from_gmv_max_only: true,
};

// Ranking used to pick a single policy to run when a video has multiple confirmed
// risks mapped to different policies. Lower number = higher priority (most severe
// enforcement wins), so `end_creator_relationship` beats `warn_creator`. Only the
// top-ranked policy is started for a video.
//
// ⚠️ These values are copied onto `safety_policies.priority` at creation time.
// Editing a number here ONLY affects new policies — you MUST also run a DB update
// for existing rows: UPDATE safety_policies SET priority = <new> WHERE type = '<type>';
export const SAFETY_POLICY_PRIORITY: Record<SafetyPolicyType, number> = {
  end_creator_relationship: 1,
  end_linked_collaboration: 2,
  request_video_removal: 3,
  request_metadata_edit: 4,
  warn_and_remove_from_gmv_max: 5,
  warn_creator: 6,
  remove_from_gmv_max_only: 7,
  // Lowest priority: purely informational, so any real enforcement policy wins
  // when a video has multiple confirmed risks mapped to different policies.
  no_action_needed: 8,
};

// UNB-2025 — single source of truth for what actionType a resolve_risk step writes to
// action_records when it closes out a risk. Policies with 2 arms (request_video_removal /
// request_metadata_edit) hold a Record<branch, actionType> instead of a plain string — see
// the inline comments on SAFETY_POLICY_CONFIG (config.ts) confirming branch "taken" means
// the creator complied (video removed / metadata edited).
const SAFETY_POLICY_RESOLVE_ACTION_TYPE: Record<SafetyPolicyType, string | Record<SequenceBranch, string>> = {
  warn_and_remove_from_gmv_max: "warned_and_removed_gmv_max",
  warn_creator: "warning_sent",
  remove_from_gmv_max_only: "removed_gmv_max",
  end_creator_relationship: "relationship_ended",
  end_linked_collaboration: "remove_from_collaboration",
  no_action_needed: "no_action_required",
  request_video_removal: { taken: "video_removed", not_taken: "remove_from_collaboration" },
  request_metadata_edit: { taken: "metadata_changed", not_taken: "remove_from_collaboration" },
};

function policyOutcome(type: SafetyPolicyType, branch: SequenceBranch | null): string {
  const entry = SAFETY_POLICY_RESOLVE_ACTION_TYPE[type];
  if (typeof entry === "string") return entry;
  return branch ? entry[branch] : "risk_resolved"; // branch not yet evaluated — generic fallback
}

export function resolveRiskActionType(
  type: SafetyPolicyType,
  branch: SequenceBranch | null,
  collabRemovalSkipped = false,
): string {
  const actionType = policyOutcome(type, branch);
  return collabRemovalSkipped && actionType === "remove_from_collaboration" ? "removed_gmv_max" : actionType;
}
